import React, { useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";

import PropTypes from "prop-types";

import { ExclamationIconRound, WarningIcon } from "../../packages/SVGs";
import WPButton from "../../packages/WPButton";
import WPHeading from "../../packages/WPHeading";
import { WPTypography } from "../../packages/WPTypography";

import WPLoader from "../WPLoader";

import { CustomModalButtons } from "../../constants/global.constants";

import { InfoCircle } from "../../assets/icons";

import CheckCircleIcon from "../../common/icons/CheckCircleIcon";
import StyledModal from "../../common/styles/components/modals/CustomModal";

import { combineClassNames } from "../../utils/appUtils";

import "./index.css";

export const DIALOG_TYPES = {
  ERROR: "danger",
  WARNING: "warning",
  SUCCESS: "success",
  INFO: "info",
};

const ICONS = {
  [DIALOG_TYPES.WARNING]: <WarningIcon />,
  [DIALOG_TYPES.ERROR]: <ExclamationIconRound />,
  [DIALOG_TYPES.SUCCESS]: <CheckCircleIcon />,
  [DIALOG_TYPES.INFO]: <InfoCircle />,
};

/**
 * ConfirmationPopup is a reusable modal component that displays a confirmation dialog.
 * It shows a header, an optional description, and buttons for confirming or canceling an action.
 * The `dangerouslySetInnerHTML` attribute is used to set the inner HTML of the description.
 * DOMPurify is used to sanitize the HTML content to prevent XSS attacks.
 */
const ConfirmationPopup = ({
  header,
  type,
  onClose,
  description,
  confirmBtn,
  cancelBtn,
  linkBtn,
  wrapContent,
  animation,
  customBackdrop,
  width,
  isLoading,
  loadingText,
  customClassName,
}) => {
  const cancelBtnRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const linkBtnRef = useRef(null);

  const getButtonType = () => {
    if (type === "success" || type === "info") return "primary";
    if (type === "danger") return "danger";
    if (type === "warning") return "warning";
    return "primary";
  };

  const buttonType = getButtonType();

  useEffect(() => {
    if (confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, []);

  const handleClick = ({ target }) => onClose(target.id);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      /* I added this because some of the modals not getting closed when clicked "Enter" 
      due to presence of any form elements in the wrapper modal that might be causing
       the Enter key to submit the form instead of triggering the handler */
      event.preventDefault();
      handleClick(event);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <>
      {customBackdrop && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(187, 177, 177, 0.5)",
            zIndex: 1040,
            pointerEvents: "none",
          }}
        />
      )}
      <StyledModal
        show
        centered
        onHide={onClose}
        backdrop="static"
        maxWidth={width || "416px"}
        className={combineClassNames("confirmation-popup", customClassName)}
        animation={animation}
      >
        {isLoading && <WPLoader loadingMsg={loadingText} />}
        <Modal.Body>
          <div className="message-pop-up">
            {type && <div className="icon-container">{ICONS[type]}</div>}
            <div className="message-margin">
              <WPHeading
                text={header}
                type="default"
                wrapContent={wrapContent}
                className="popup-header"
              />
              {description && (
                <WPTypography
                  className="description-container break-spaces fs-14"
                  text={description}
                  renderHtml
                />
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {linkBtn && (
            <WPButton
              variant="text"
              type={buttonType}
              id={CustomModalButtons.CLOSE_BTN}
              onClick={handleClick}
              ref={linkBtnRef} // Attach the ref to the close button
            >
              {linkBtn}
            </WPButton>
          )}
          {cancelBtn && (
            <WPButton
              variant="outlined"
              type={buttonType}
              id={CustomModalButtons.CANCEL}
              onClick={handleClick}
              ref={cancelBtnRef} // Attach the ref to the cancel button
            >
              {cancelBtn}
            </WPButton>
          )}
          {confirmBtn && (
            <WPButton
              type={buttonType}
              id={CustomModalButtons.CONFIRM}
              onClick={handleClick}
              ref={confirmBtnRef} // Attach the ref to the confirm button
            >
              {confirmBtn}
            </WPButton>
          )}
        </Modal.Footer>
      </StyledModal>
    </>
  );
};

ConfirmationPopup.propTypes = {
  header: PropTypes.string,
  type: PropTypes.oneOf(Object.keys(ICONS)),
  onClose: PropTypes.func.isRequired,
  description: PropTypes.string,
  confirmBtn: PropTypes.string,
  cancelBtn: PropTypes.string,
  wrapContent: PropTypes.bool,
  animation: PropTypes.bool,
  customBackdrop: PropTypes.bool,
  width: PropTypes.string,
  customClassName: PropTypes.string,
};

ConfirmationPopup.defaultProps = {
  wrapContent: false,
  animation: true,
};

export default ConfirmationPopup;
